
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Topics', icon: 'ri-apps-line' },
    { id: 'getting-started', name: 'Getting Started', icon: 'ri-rocket-line' },
    { id: 'booking', name: 'Booking & Payments', icon: 'ri-calendar-check-line' },
    { id: 'hosting', name: 'Hosting', icon: 'ri-home-smile-line' },
    { id: 'safety', name: 'Safety & Security', icon: 'ri-shield-check-line' },
    { id: 'account', name: 'Account Settings', icon: 'ri-user-settings-line' },
  ];

  const articles = [
    {
      category: 'getting-started',
      title: 'How to Find Storage Space on Stashly',
      description: 'Learn how to search, filter, and book the perfect storage space for your needs.',
      icon: 'ri-search-line',
    },
    {
      category: 'getting-started',
      title: 'Creating Your Stashly Account',
      description: 'Step-by-step guide to setting up your account and getting verified.',
      icon: 'ri-user-add-line',
    },
    {
      category: 'booking',
      title: 'How to Book a Storage Space',
      description: 'Complete guide to making your first booking and what to expect.',
      icon: 'ri-calendar-line',
    },
    {
      category: 'booking',
      title: 'Payment Methods & Billing',
      description: 'Understanding accepted payment methods, billing cycles, and refunds.',
      icon: 'ri-bank-card-line',
    },
    {
      category: 'booking',
      title: 'Cancellation & Refund Policy',
      description: 'Learn about our cancellation policy and how to request refunds.',
      icon: 'ri-refund-line',
    },
    {
      category: 'hosting',
      title: 'Listing Your Space on Stashly',
      description: 'How to create an attractive listing and set competitive pricing.',
      icon: 'ri-add-box-line',
    },
    {
      category: 'hosting',
      title: 'Host Protection & Insurance',
      description: 'Understanding your coverage and how to file claims if needed.',
      icon: 'ri-shield-star-line',
    },
    {
      category: 'hosting',
      title: 'Managing Bookings as a Host',
      description: 'Tips for communicating with renters and managing your calendar.',
      icon: 'ri-calendar-todo-line',
    },
    {
      category: 'hosting',
      title: 'Pricing Your Storage Space',
      description: 'Strategies for competitive pricing and maximising your earnings.',
      icon: 'ri-money-dollar-circle-line',
    },
    {
      category: 'safety',
      title: 'Safety Guidelines for Renters',
      description: 'Best practices for storing your items safely and securely.',
      icon: 'ri-shield-user-line',
    },
    {
      category: 'safety',
      title: 'Safety Guidelines for Hosts',
      description: 'How to protect your property and screen potential renters.',
      icon: 'ri-shield-check-line',
    },
    {
      category: 'safety',
      title: 'Insurance Coverage Explained',
      description: 'Detailed breakdown of what is and isn\'t covered by our insurance.',
      icon: 'ri-file-shield-line',
    },
    {
      category: 'account',
      title: 'Updating Your Profile Information',
      description: 'How to edit your personal details, photos, and preferences.',
      icon: 'ri-profile-line',
    },
    {
      category: 'account',
      title: 'Verification Process',
      description: 'Understanding identity verification and why it matters.',
      icon: 'ri-verified-badge-line',
    },
    {
      category: 'account',
      title: 'Managing Notifications',
      description: 'Customise your email and SMS notification preferences.',
      icon: 'ri-notification-3-line',
    },
  ];

  const filteredArticles = articles.filter(article => {
    const matchesCategory = activeCategory === 'all' || article.category === activeCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#14B8A6] to-[#0F9B8E] text-white py-20 pt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">How Can We Help You?</h1>
          <p className="text-xl mb-8 text-white/90">
            Search our knowledge base for answers to common questions
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 pl-14 rounded-full text-gray-900 text-lg focus:outline-none focus:ring-4 focus:ring-white/30"
              />
              <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-2xl text-gray-400"></i>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a href="/contact" className="flex items-center gap-4 p-6 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-600 text-white rounded-full text-2xl">
                <i className="ri-customer-service-2-line"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Contact Support</h3>
                <p className="text-sm text-gray-600">Get help from our team</p>
              </div>
            </a>
            
            <a href="/faq" className="flex items-center gap-4 p-6 bg-green-50 rounded-xl hover:bg-green-100 transition-colors cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-green-600 text-white rounded-full text-2xl">
                <i className="ri-question-answer-line"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">FAQss</h3>
                <p className="text-sm text-gray-600">Quick answers to common questions</p>
              </div>
            </a>
            
            <a href="/trust-safety" className="flex items-center gap-4 p-6 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-600 text-white rounded-full text-2xl">
                <i className="ri-shield-check-line"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Trust &amp; Safety</h3>
                <p className="text-sm text-gray-600">Learn about our safety measures</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === category.id
                    ? 'bg-[#14B8A6] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={`${category.icon} mr-2`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            {activeCategory === 'all' ? 'All Articles' : categories.find(c => c.id === activeCategory)?.name}
          </h2>
          
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#14B8A6]/10 text-[#14B8A6] rounded-lg text-2xl mb-4">
                    <i className={article.icon}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{article.title}</h3>
                  <p className="text-gray-600 mb-4">{article.description}</p>
                  <a href="#" className="text-[#14B8A6] font-semibold hover:text-[#0F9B8E] inline-flex items-center cursor-pointer">
                    Read More
                    <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Articles Found</h3>
              <p className="text-gray-600">Try adjusting your search or browse all categories</p>
            </div>
          )}
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Need Help?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Our support team is here to assist you with any questions
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-line mr-2"></i>
            Contact Support
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
